import React from 'react';
import { X, CheckCircle, Copy } from 'lucide-react';

const IntegrationManualConfigModal = ({ integration, isOpen, onClose }) => {
  if (!isOpen || !integration) return null;

  const configurations = {
    elasticsearch: {
      title: "Elasticsearch",
      icon: "🔍",
      steps: [
        {
          title: "1. Installer Elasticsearch",
          commands: [
            "wget -qO - https://artifacts.elastic.co/GPG-KEY-elasticsearch | sudo apt-key add -",
            "sudo apt-get install apt-transport-https",
            "echo \"deb https://artifacts.elastic.co/packages/8.x/apt stable main\" | sudo tee /etc/apt/sources.list.d/elastic-8.x.list",
            "sudo apt-get update && sudo apt-get install elasticsearch"
          ]
        },
        {
          title: "2. Configurer Elasticsearch",
          commands: [
            "sudo nano /etc/elasticsearch/elasticsearch.yml",
            "# Ajouter:",
            "network.host: 0.0.0.0",
            "discovery.type: single-node",
            "xpack.security.enabled: false"
          ]
        },
        {
          title: "3. Démarrer le service",
          commands: [
            "sudo systemctl start elasticsearch",
            "sudo systemctl enable elasticsearch",
            "curl http://localhost:9200"
          ]
        },
        {
          title: "4. Configurer dans SYLINK",
          params: [
            { key: "ES_HOST", value: "localhost:9200" },
            { key: "ES_INDEX", value: "sylink-scans" },
            { key: "ES_USERNAME", value: "elastic (si auth activée)" },
            { key: "ES_PASSWORD", value: "votre_password" }
          ]
        }
      ]
    },
    slack: {
      title: "Slack",
      icon: "💬",
      steps: [
        {
          title: "1. Créer Slack App",
          instructions: [
            "Aller sur https://api.slack.com/apps",
            "Cliquer 'Create New App'",
            "Choisir 'From scratch'",
            "Nom: SYLINK Security Scanner",
            "Workspace: Sélectionner votre workspace"
          ]
        },
        {
          title: "2. Activer Incoming Webhooks",
          instructions: [
            "Dans l'app, aller à 'Incoming Webhooks'",
            "Activer 'Activate Incoming Webhooks'",
            "Cliquer 'Add New Webhook to Workspace'",
            "Sélectionner channel (ex: #security)",
            "Copier l'URL du webhook"
          ]
        },
        {
          title: "3. Configurer dans SYLINK",
          params: [
            { key: "SLACK_WEBHOOK_URL", value: "https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXX" },
            { key: "SLACK_CHANNEL", value: "#security" }
          ]
        },
        {
          title: "4. Tester",
          commands: [
            "curl -X POST https://hooks.slack.com/services/YOUR_WEBHOOK \\",
            "  -H 'Content-Type: application/json' \\",
            "  -d '{\"text\":\"Test SYLINK\"}'"
          ]
        }
      ]
    },
    teams: {
      title: "Microsoft Teams",
      icon: "👥",
      steps: [
        {
          title: "1. Créer Incoming Webhook Teams",
          instructions: [
            "Ouvrir Teams",
            "Aller dans le channel souhaité",
            "Cliquer sur '...' → Connectors",
            "Rechercher 'Incoming Webhook'",
            "Cliquer 'Add' puis 'Configure'",
            "Nom: SYLINK Alerts",
            "Upload image (optionnel)",
            "Créer → Copier l'URL"
          ]
        },
        {
          title: "2. Configurer dans SYLINK",
          params: [
            { key: "TEAMS_WEBHOOK_URL", value: "https://outlook.office.com/webhook/..." }
          ]
        },
        {
          title: "3. Format message",
          example: {
            "@type": "MessageCard",
            "@context": "http://schema.org/extensions",
            "summary": "SYLINK Alert",
            "themeColor": "99ce16",
            "title": "Scan Completed",
            "text": "18 hosts discovered"
          }
        }
      ]
    },
    discord: {
      title: "Discord",
      icon: "🎮",
      steps: [
        {
          title: "1. Créer Webhook Discord",
          instructions: [
            "Ouvrir Discord",
            "Server Settings → Integrations",
            "Webhooks → New Webhook",
            "Nom: SYLINK",
            "Channel: #security",
            "Copy Webhook URL"
          ]
        },
        {
          title: "2. Configurer dans SYLINK",
          params: [
            { key: "DISCORD_WEBHOOK_URL", value: "https://discord.com/api/webhooks/..." }
          ]
        }
      ]
    },
    s3: {
      title: "AWS S3",
      icon: "☁️",
      steps: [
        {
          title: "1. Créer Bucket S3",
          instructions: [
            "AWS Console → S3",
            "Create bucket",
            "Nom: sylink-reports-{votre-entreprise}",
            "Région: eu-west-1",
            "Block public access: ON"
          ]
        },
        {
          title: "2. Créer IAM User",
          instructions: [
            "IAM → Users → Add user",
            "Nom: sylink-s3-uploader",
            "Access type: Programmatic",
            "Permissions: AmazonS3FullAccess",
            "Créer → Copier Access Key + Secret"
          ]
        },
        {
          title: "3. Configurer dans SYLINK",
          params: [
            { key: "AWS_ACCESS_KEY_ID", value: "AKIAIOSFODNN7EXAMPLE" },
            { key: "AWS_SECRET_ACCESS_KEY", value: "wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY" },
            { key: "AWS_S3_BUCKET", value: "sylink-reports-{entreprise}" },
            { key: "AWS_REGION", value: "eu-west-1" }
          ]
        },
        {
          title: "4. Installer AWS CLI",
          commands: [
            "pip install boto3",
            "aws configure"
          ]
        }
      ]
    },
    prometheus: {
      title: "Prometheus",
      icon: "📊",
      steps: [
        {
          title: "1. Installer Prometheus",
          commands: [
            "docker run -d --name prometheus \\",
            "  -p 9090:9090 \\",
            "  -v /etc/prometheus:/etc/prometheus \\",
            "  prom/prometheus"
          ]
        },
        {
          title: "2. Configurer Scrape",
          file: "/etc/prometheus/prometheus.yml",
          content: `scrape_configs:
  - job_name: 'sylink'
    static_configs:
      - targets: ['192.168.1.141:8001']
    metrics_path: '/metrics'
    scrape_interval: 15s`
        },
        {
          title: "3. Exposer métriques SYLINK",
          info: "Endpoint /metrics déjà configuré dans backend"
        }
      ]
    },
    kafka: {
      title: "Apache Kafka",
      icon: "🔄",
      steps: [
        {
          title: "1. Installer Kafka (Docker)",
          commands: [
            "docker run -d --name zookeeper -p 2181:2181 zookeeper",
            "docker run -d --name kafka -p 9092:9092 \\",
            "  --link zookeeper \\",
            "  -e KAFKA_ZOOKEEPER_CONNECT=zookeeper:2181 \\",
            "  -e KAFKA_ADVERTISED_LISTENERS=PLAINTEXT://localhost:9092 \\",
            "  confluentinc/cp-kafka"
          ]
        },
        {
          title: "2. Créer Topic",
          commands: [
            "docker exec -it kafka kafka-topics --create \\",
            "  --topic sylink-events \\",
            "  --bootstrap-server localhost:9092 \\",
            "  --partitions 3 \\",
            "  --replication-factor 1"
          ]
        },
        {
          title: "3. Configurer dans SYLINK",
          params: [
            { key: "KAFKA_BOOTSTRAP_SERVERS", value: "localhost:9092" },
            { key: "KAFKA_TOPIC", value: "sylink-events" }
          ]
        }
      ]
    },
    glpi: {
      title: "GLPI",
      icon: "🖥️",
      steps: [
        {
          title: "1. Activer l'API REST GLPI",
          instructions: [
            "Aller dans Configuration → Générale → API",
            "Activer 'Enable Rest API'",
            "Activer 'Enable login with credentials'",
            "Sauvegarder"
          ]
        },
        {
          title: "2. Créer un utilisateur API",
          instructions: [
            "Administration → Utilisateurs → Ajouter",
            "Nom: sylink-api",
            "Profil: Technicien ou Super-Admin",
            "Activer 'Autorisation à distance'"
          ]
        },
        {
          title: "3. Récupérer le Token",
          commands: [
            "curl -X GET \\",
            "  'https://glpi.exemple.com/apirest.php/initSession' \\",
            "  -H 'Authorization: user_token YOUR_TOKEN' \\",
            "  -H 'App-Token: YOUR_APP_TOKEN'"
          ]
        },
        {
          title: "4. Configurer dans SYLINK",
          params: [
            { key: "GLPI_URL", value: "https://glpi.exemple.com" },
            { key: "GLPI_USER_TOKEN", value: "votre_user_token" },
            { key: "GLPI_APP_TOKEN", value: "votre_app_token" },
            { key: "GLPI_ENTITY_ID", value: "0" }
          ]
        }
      ]
    },
    servicenow: {
      title: "ServiceNow",
      icon: "⚡",
      steps: [
        {
          title: "1. Créer une Application Registry",
          instructions: [
            "System OAuth → Application Registry",
            "Créer 'External OAuth Client'",
            "Nom: SYLINK Integration",
            "Client ID auto-généré",
            "Redirect URL: https://sylink.exemple.com/oauth/callback"
          ]
        },
        {
          title: "2. Configurer l'utilisateur d'intégration",
          instructions: [
            "User Administration → Users",
            "Créer un nouvel utilisateur: sylink.integration",
            "Assigner rôle: itil, rest_api_explorer",
            "Désactiver MFA pour ce compte"
          ]
        },
        {
          title: "3. Configurer dans SYLINK",
          params: [
            { key: "SERVICENOW_INSTANCE", value: "dev12345.service-now.com" },
            { key: "SERVICENOW_CLIENT_ID", value: "your_client_id" },
            { key: "SERVICENOW_CLIENT_SECRET", value: "your_client_secret" },
            { key: "SERVICENOW_USERNAME", value: "sylink.integration" },
            { key: "SERVICENOW_PASSWORD", value: "your_password" }
          ]
        },
        {
          title: "4. Tester la connexion",
          commands: [
            "curl -X GET \\",
            "  'https://INSTANCE.service-now.com/api/now/table/incident?sysparm_limit=1' \\",
            "  -u 'username:password' \\",
            "  -H 'Accept: application/json'"
          ]
        }
      ]
    },
    sekoia: {
      title: "Sekoia.io",
      icon: "🔐",
      steps: [
        {
          title: "1. Créer une API Key Sekoia",
          instructions: [
            "Se connecter à app.sekoia.io",
            "Aller dans Settings → API Keys",
            "Créer une nouvelle clé API",
            "Nom: SYLINK Integration",
            "Permissions: Events:Write, Alerts:Read"
          ]
        },
        {
          title: "2. Récupérer l'identifiant d'intake",
          instructions: [
            "Operations Center → Intakes",
            "Créer ou sélectionner un intake",
            "Type: Custom Format ou Generic Syslog",
            "Copier l'Intake Key"
          ]
        },
        {
          title: "3. Configurer dans SYLINK",
          params: [
            { key: "SEKOIA_API_KEY", value: "votre_api_key" },
            { key: "SEKOIA_INTAKE_KEY", value: "votre_intake_key" },
            { key: "SEKOIA_INTAKE_URL", value: "https://intake.sekoia.io" }
          ]
        },
        {
          title: "4. Format d'événement",
          example: {
            "timestamp": "2024-01-15T10:30:00Z",
            "source": "SYLINK",
            "event_type": "vulnerability_detected",
            "severity": "high",
            "target": "192.168.1.100",
            "vulnerability": "CVE-2024-XXXX"
          }
        }
      ]
    },
    grafana: {
      title: "Grafana",
      icon: "📈",
      steps: [
        {
          title: "1. Installer Grafana",
          commands: [
            "docker run -d --name grafana \\",
            "  -p 3000:3000 \\",
            "  -v grafana-storage:/var/lib/grafana \\",
            "  grafana/grafana"
          ]
        },
        {
          title: "2. Ajouter Prometheus comme Data Source",
          instructions: [
            "Se connecter à Grafana (admin/admin)",
            "Configuration → Data Sources → Add",
            "Type: Prometheus",
            "URL: http://prometheus:9090",
            "Sauvegarder & Tester"
          ]
        },
        {
          title: "3. Importer le Dashboard SYLINK",
          instructions: [
            "Dashboards → Import",
            "Coller l'ID du dashboard SYLINK: 12345",
            "Ou charger le fichier JSON du dashboard",
            "Sélectionner la source de données Prometheus"
          ]
        },
        {
          title: "4. Configurer les alertes Grafana",
          params: [
            { key: "GRAFANA_URL", value: "http://localhost:3000" },
            { key: "GRAFANA_API_KEY", value: "eyJrIjoiT0tTcG1..." },
            { key: "GRAFANA_ORG_ID", value: "1" }
          ]
        }
      ]
    },
    wazuh: {
      title: "Wazuh SIEM",
      icon: "🛡️",
      steps: [
        {
          title: "1. Installer Wazuh Manager",
          commands: [
            "curl -sO https://packages.wazuh.com/4.7/wazuh-install.sh",
            "sudo bash wazuh-install.sh -a"
          ]
        },
        {
          title: "2. Créer API User",
          instructions: [
            "Wazuh UI → Settings → API",
            "Create user → sylink",
            "Permissions: Read, Write",
            "Générer API token"
          ]
        },
        {
          title: "3. Configurer dans SYLINK",
          params: [
            { key: "WAZUH_API_URL", value: "https://localhost:55000" },
            { key: "WAZUH_API_TOKEN", value: "eyJhbGciOiJIUzI1NiIsInR5cCI6..." },
            { key: "WAZUH_INDEX", value: "wazuh-sylink" }
          ]
        }
      ]
    }
  };

  const config = configurations[integration] || {};

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copié!');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-brand-black-light border-2 border-brand-green/30 rounded-2xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="p-6 border-b border-brand-green/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{config.icon}</span>
            <div>
              <h2 className="text-2xl font-bold text-gradient-green">{config.title}</h2>
              <p className="text-gray-400">Configuration manuelle requise</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-brand-black rounded-lg">
            <X className="w-6 h-6 text-gray-400 hover:text-brand-green" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {config.steps?.map((step, index) => (
            <div key={index} className="bg-brand-black rounded-lg p-5 border border-gray-800">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-brand-green" />
                {step.title}
              </h3>

              {/* Instructions */}
              {step.instructions && (
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
                  {step.instructions.map((inst, i) => (
                    <li key={i}>{inst}</li>
                  ))}
                </ol>
              )}

              {/* Commands */}
              {step.commands && (
                <div className="space-y-2">
                  {step.commands.map((cmd, i) => (
                    <div key={i} className="relative group">
                      <pre className="bg-brand-black p-3 rounded font-mono text-sm text-brand-green overflow-x-auto">
                        {cmd}
                      </pre>
                      <button
                        onClick={() => copyToClipboard(cmd)}
                        className="absolute top-2 right-2 p-1.5 bg-brand-black-lighter rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Copy className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Params */}
              {step.params && (
                <div className="space-y-2 mt-3">
                  {step.params.map((param, i) => (
                    <div key={i} className="flex items-center gap-3 bg-brand-black-lighter p-3 rounded">
                      <span className="text-gray-400 text-sm w-48">{param.key}:</span>
                      <code className="flex-1 text-brand-green text-xs font-mono">{param.value}</code>
                      <button onClick={() => copyToClipboard(param.value)}>
                        <Copy className="w-4 h-4 text-gray-400 hover:text-brand-green" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* File content */}
              {step.file && step.content && (
                <div className="mt-3">
                  <div className="text-sm text-gray-400 mb-2">Fichier: {step.file}</div>
                  <pre className="bg-brand-black p-3 rounded font-mono text-xs text-brand-green overflow-x-auto">
                    {step.content}
                  </pre>
                </div>
              )}

              {/* Example */}
              {step.example && (
                <div className="mt-3">
                  <div className="text-sm text-gray-400 mb-2">Exemple de payload:</div>
                  <pre className="bg-brand-black p-3 rounded font-mono text-xs text-brand-green overflow-x-auto">
                    {JSON.stringify(step.example, null, 2)}
                  </pre>
                </div>
              )}

              {/* Info */}
              {step.info && (
                <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                  <div className="text-sm text-blue-300">{step.info}</div>
                </div>
              )}
            </div>
          ))}

          {/* Note finale */}
          <div className="bg-brand-green/10 border border-brand-green/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-brand-green mt-0.5">💡</div>
              <div className="text-sm text-gray-300">
                <div className="font-medium text-brand-green mb-1">Aide</div>
                <div>
                  Après configuration, redémarrez le backend SYLINK pour appliquer les changements.
                  Les credentials peuvent être ajoutés dans le fichier .env ou via cette interface.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-brand-green/20 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Fermer
          </button>
          <button
            className="btn-green"
          >
            Marquer comme Configuré
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationManualConfigModal;
